import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Button, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import "./ProfileQuestionnaire.css";

const MAX_LENGTH = 1000;

const ProfileQuestionnaire = (props) => {
    const {
        sections,
        initialValues = {},
        onSaveAndContinue,
        isSaving,
    } = props;
    const { t } = useTranslation();

    const [answers, setAnswers] = useState(initialValues);
    const [expandedSection, setExpandedSection] = useState(sections[0]?.id);

    const isQuestionCompleted = (questionId) => !!(answers[questionId]?.trim());

    const isSectionCompleted = (section) => (section?.questions || []).every(({ id }) => isQuestionCompleted(id));

    const isProfileCompleted = useMemo(
        () => sections.every((section) => isSectionCompleted(section)),
        [answers, sections],
    );

    const handleChange = (questionId) => (event) => {
        setAnswers((current) => ({
            ...current,
            [questionId]: event.target.value,
        }));
    };

    const handleAccordionChange = (sectionId) => (_, isExpanded) => {
        setExpandedSection(isExpanded ? sectionId : false);
    };

    const handleNextSection = (sectionIndex) => {
        const nextSection = sections[sectionIndex + 1];

        if (nextSection) {
            setExpandedSection(nextSection.id);
        }
    };

    return (
        <div className="profile-questionnaire">
            <div className="profile-questionnaire__sections">
                {sections.map((section, sectionIndex) => {
                    const isCompleted = isSectionCompleted(section);
                    const isExpanded = expandedSection === section.id;

                    return (
                        <Accordion
                            key={section.id}
                            expanded={isExpanded}
                            onChange={handleAccordionChange(section.id)}
                            className="profile-questionnaire__section"
                            disableGutters
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`${section.id}-content`}
                                id={`${section.id}-header`}
                            >
                                <div className={`profile-questionnaire__section-number ${isCompleted ? "profile-questionnaire__section-number--completed" : ""}`}>
                                    {sectionIndex + 1}
                                </div>

                                <Typography
                                    variant="h3"
                                    className="profile-questionnaire__section-title text-primary"
                                >
                                    {section.title}
                                </Typography>

                                <div className="profile-questionnaire__section-status">
                                    {isCompleted ? (
                                        <Typography
                                            variant="body1"
                                            className="profile-questionnaire__completed"
                                        >
                                            {t("mentorPathway.completed")}
                                        </Typography>
                                    ) : (
                                        <>
                                            <AccessTimeOutlinedIcon />

                                            <Typography variant="body1">
                                                {t("mentorPathway.estimatedTime", {
                                                    count: section.estimatedTime,
                                                })}
                                            </Typography>
                                        </>
                                    )}
                                </div>
                            </AccordionSummary>

                            <AccordionDetails>
                                <div className="profile-questionnaire__questions">
                                    {section.questions.map((question) => {
                                        const value = answers[question.id] || "";

                                        return (
                                            <div
                                                key={question.id}
                                                className="profile-questionnaire__question"
                                            >
                                                <div className="profile-questionnaire__question__icon">
                                                    <QuestionMarkIcon />
                                                </div>
                                                <div className="profile-questionnaire__question__main">
                                                    <Typography
                                                        variant="h4"
                                                        className="profile-questionnaire__question-title text-primary"
                                                    >
                                                        {question.title}
                                                    </Typography>

                                                    <Typography
                                                        variant="body1"
                                                        className="profile-questionnaire__question-description"
                                                    >
                                                        {question.description}
                                                    </Typography>

                                                    <TextField
                                                        multiline
                                                        minRows={4}
                                                        fullWidth
                                                        value={value}
                                                        onChange={handleChange(question.id)}
                                                        placeholder={t("mentorPathway.answerPlaceholder")}
                                                        slotProps={{
                                                            htmlInput: { maxLength: MAX_LENGTH },
                                                        }}
                                                        helperText={`${value.length} / ${MAX_LENGTH}`}
                                                        className="profile-questionnaire__answer"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {sectionIndex < sections.length - 1 && (
                                    <div className="profile-questionnaire__section-footer">
                                        <Button
                                            variant="contained"
                                            onClick={() => handleNextSection(sectionIndex)}
                                        >
                                            {t("mentorPathway.nextSection")}
                                        </Button>
                                    </div>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </div>
            <div className="profile-questionnaire__actions">
                <Button
                    variant="contained"
                    loading={isSaving}
                    disabled={!isProfileCompleted}
                    onClick={() => onSaveAndContinue?.(answers)}
                    className="profile-questionnaire__continue-button"
                >
                    {t("mentorPathway.saveAndContinue")}
                </Button>
            </div>
        </div>
    );
};

export default ProfileQuestionnaire;
